import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import DeleteInternalOrderDialog from '../components/DeleteInternalOrderDialog';
import InternalOrderCertDialog from '../components/InternalOrderCertDialog';
import { apiService } from '../services/api';
import { useAuth } from '../services/AuthContext';
import { InternalOrderCoilLink, InternalOrderDetail } from '../types';
import { apiErrorMessage } from '../utils/apiError';

type ToastSeverity = 'success' | 'info' | 'error';

interface ToastState {
  open: boolean;
  message: string;
  severity: ToastSeverity;
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-DE');
  } catch {
    return iso;
  }
}

function groupByCharge(links: InternalOrderCoilLink[]): {
  charge_id: number;
  charge_number: string;
  links: InternalOrderCoilLink[];
}[] {
  const buckets = new Map<number, { charge_number: string; links: InternalOrderCoilLink[] }>();
  for (const link of links) {
    const existing = buckets.get(link.charge_id);
    if (existing) {
      existing.links.push(link);
    } else {
      buckets.set(link.charge_id, { charge_number: link.charge_number, links: [link] });
    }
  }
  return Array.from(buckets.entries())
    .map(([charge_id, v]) => ({
      charge_id,
      charge_number: v.charge_number,
      links: v.links,
    }))
    .sort((a, b) => a.charge_number.localeCompare(b.charge_number));
}

export default function ProductionScanPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const orderId = Number(id);

  const [order, setOrder] = useState<InternalOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [scanValue, setScanValue] = useState('');
  const [scanFlash, setScanFlash] = useState<'idle' | 'success' | 'error'>('idle');
  const scanInputRef = useRef<HTMLInputElement | null>(null);

  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [confirmUnlinkCoilId, setConfirmUnlinkCoilId] = useState<number | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [closing, setClosing] = useState(false);

  const { isKalotteUser } = useAuth();
  const [certDialogOpen, setCertDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const reload = useCallback(async () => {
    if (!orderId || Number.isNaN(orderId)) return;
    try {
      const detail = await apiService.getInternalOrder(orderId);
      setOrder(detail);
      setError(null);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Auftrag konnte nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Re-focus the scan input after every reload while the order is open.
  useEffect(() => {
    if (order && order.status === 'open') {
      scanInputRef.current?.focus();
    }
  }, [order]);

  const grouped = useMemo(() => (order ? groupByCharge(order.coil_links) : []), [order]);

  function flashAndReset(kind: 'success' | 'error') {
    setScanFlash(kind);
    setTimeout(() => setScanFlash('idle'), 250);
  }

  async function handleScan() {
    if (!order || order.status !== 'open') return;
    const trimmed = scanValue.trim();
    if (!trimmed) return;
    try {
      const res = await apiService.scanCoil(order.id, trimmed);
      setScanValue('');
      if (res.already_linked) {
        flashAndReset('success');
        setToast({
          open: true,
          message: `Schon erfasst: ${res.coil.barcode}`,
          severity: 'info',
        });
      } else {
        flashAndReset('success');
        setToast({
          open: true,
          message: `Coil erfasst: ${res.coil.barcode}`,
          severity: 'success',
        });
        await reload();
      }
    } catch (err: any) {
      setScanValue('');
      flashAndReset('error');
      const detail = apiErrorMessage(err, 'Scan fehlgeschlagen.');
      setToast({ open: true, message: detail, severity: 'error' });
    } finally {
      // Always refocus, no matter what.
      setTimeout(() => scanInputRef.current?.focus(), 0);
    }
  }

  async function handleUnlink(coilId: number) {
    if (!order) return;
    try {
      await apiService.unlinkCoil(order.id, coilId);
      setToast({ open: true, message: 'Coil entfernt.', severity: 'success' });
      await reload();
    } catch (err: any) {
      const detail = apiErrorMessage(err, 'Entfernen fehlgeschlagen.');
      setToast({ open: true, message: detail, severity: 'error' });
    } finally {
      setConfirmUnlinkCoilId(null);
    }
  }

  async function handleClose() {
    if (!order) return;
    setClosing(true);
    try {
      await apiService.closeInternalOrder(order.id);
      setToast({ open: true, message: 'Auftrag abgeschlossen.', severity: 'success' });
      await reload();
    } catch (err: any) {
      const detail = apiErrorMessage(err, 'Abschließen fehlgeschlagen.');
      setToast({ open: true, message: detail, severity: 'error' });
    } finally {
      setClosing(false);
      setConfirmClose(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/production')}>
          Zurück
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error ?? 'Auftrag nicht gefunden.'}
        </Alert>
      </Box>
    );
  }

  const isOpen = order.status === 'open';
  const isMixed = order.grade_count > 1;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/production')} sx={{ mb: 2 }}>
        Zurück
      </Button>

      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h4" fontFamily="monospace">
            {order.auftragsnummer}
          </Typography>
          <Chip label={isOpen ? 'Offen' : 'Abgeschlossen'} color={isOpen ? 'success' : 'default'} />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {isKalotteUser && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlinedIcon />}
              onClick={() => setConfirmDelete(true)}
            >
              Auftrag löschen
            </Button>
          )}
          {isOpen ? (
            <Button
              variant="contained"
              color="primary"
              disabled={order.coil_count === 0 || closing}
              onClick={() => setConfirmClose(true)}
            >
              Auftrag abschließen
            </Button>
          ) : (
            <>
              <Chip icon={<LockOutlinedIcon />} label="Bearbeiten gesperrt" />
              {isKalotteUser && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={() => setCertDialogOpen(true)}
                >
                  Zeugnis herunterladen
                </Button>
              )}
            </>
          )}
        </Stack>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 2, color: 'text.secondary' }}>
        <Typography variant="body2">Erstellt: {formatDateTime(order.created_at)}</Typography>
        {order.closed_at && (
          <Typography variant="body2">Abgeschlossen: {formatDateTime(order.closed_at)}</Typography>
        )}
      </Stack>

      {/* Werkstoffe-Panel */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <Typography variant="subtitle2" sx={{ mr: 1 }}>
            Werkstoffe:
          </Typography>
          {order.grades.length === 0 ? (
            <Typography color="text.secondary">noch keine Coils erfasst</Typography>
          ) : (
            order.grades.map((g) => (
              <Chip
                key={g.material_grade_id}
                label={`${g.name} ×${g.coil_count}`}
                variant="outlined"
              />
            ))
          )}
          {isMixed && (
            <Tooltip title="Dieser Auftrag enthält Coils unterschiedlicher Werkstoffe.">
              <Chip
                icon={<InfoOutlinedIcon />}
                label="Gemischte Werkstoffe"
                color="info"
                size="small"
              />
            </Tooltip>
          )}
          <Box sx={{ flex: 1 }} />
          {order.business_unit && <Chip label={order.business_unit} size="small" />}
        </Stack>
      </Paper>

      {/* Scan box */}
      {isOpen && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <TextField
            inputRef={scanInputRef}
            value={scanValue}
            onChange={(e) => setScanValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleScan();
              }
            }}
            label="Coil-Barcode scannen…"
            fullWidth
            autoFocus
            inputProps={{
              style: { fontFamily: 'monospace', fontSize: '1.25rem' },
              autoCapitalize: 'off',
              autoCorrect: 'off',
              spellCheck: false,
            }}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                transition: 'border-color 200ms',
                ...(scanFlash === 'success' ? { borderColor: 'success.main', borderWidth: 2 } : {}),
                ...(scanFlash === 'error' ? { borderColor: 'error.main', borderWidth: 2 } : {}),
              },
            }}
          />
        </Paper>
      )}

      {/* Coils table */}
      {grouped.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Noch keine Coils erfasst.</Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {grouped.map((group) => (
            <Paper key={group.charge_id} variant="outlined">
              <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle1" fontFamily="monospace" fontWeight={600}>
                  Charge: {group.charge_number}
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Coil-Nr</TableCell>
                      <TableCell>Barcode</TableCell>
                      <TableCell>Werkstoff</TableCell>
                      <TableCell>Erfasst</TableCell>
                      {isOpen && <TableCell align="right">Aktion</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.links.map((link) => (
                      <TableRow key={link.coil.id}>
                        <TableCell>{link.coil.coil_number}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{link.coil.barcode}</TableCell>
                        <TableCell>{link.material_grade_name}</TableCell>
                        <TableCell>{formatDateTime(link.linked_at)}</TableCell>
                        {isOpen && (
                          <TableCell align="right">
                            <Tooltip title="Coil aus Auftrag entfernen">
                              <IconButton
                                size="small"
                                onClick={() => setConfirmUnlinkCoilId(link.coil.id)}
                              >
                                <DeleteOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Unlink confirmation */}
      <Dialog open={confirmUnlinkCoilId !== null} onClose={() => setConfirmUnlinkCoilId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Coil entfernen?</DialogTitle>
        <DialogContent>
          <Typography>
            Soll der Coil aus diesem Auftrag entfernt werden? Der Coil bleibt im System erhalten und
            kann anderen Aufträgen zugeordnet werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUnlinkCoilId(null)}>Abbrechen</Button>
          <Button
            color="error"
            onClick={() => confirmUnlinkCoilId !== null && void handleUnlink(confirmUnlinkCoilId)}
          >
            Entfernen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close confirmation */}
      <Dialog open={confirmClose} onClose={() => !closing && setConfirmClose(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Auftrag abschließen?</DialogTitle>
        <DialogContent>
          <Typography>
            Nach dem Abschließen können dem Auftrag keine Coils mehr hinzugefügt oder entfernt
            werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)} disabled={closing}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => void handleClose()}
            disabled={closing}
          >
            {closing ? 'Wird abgeschlossen…' : 'Abschließen'}
          </Button>
        </DialogActions>
      </Dialog>

      <InternalOrderCertDialog
        orderId={order.id}
        auftragsnummer={order.auftragsnummer}
        grades={order.grades}
        open={certDialogOpen}
        onClose={() => setCertDialogOpen(false)}
      />

      <DeleteInternalOrderDialog
        order={confirmDelete ? order : null}
        onClose={() => setConfirmDelete(false)}
        onDeleted={() => navigate('/production')}
      />
    </Box>
  );
}
